import {
  Component,
  DestroyRef,
  signal,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormsModule } from '@angular/forms';
import {
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { ClientInfoComponent } from '../client-info/client-info.component';
import { ClientPrototypeSearchResultComponent } from '../client-prototype-search-result/client-prototype-search-result.component';
import { UiSession, ClientSessionService } from '@brighthub/uisession';
import { GenericResponseCode } from '@brighthub/api';
import { MessageService } from 'primeng/api';
import {
  distinctUntilChanged,
  map,
  Subject,
  switchMap,
  timer,
} from 'rxjs';
import { IftaLabel } from 'primeng/iftalabel';
import { ClientSearchService } from '../client-search.service';
import { ClientSearchResultItem } from '../client-search-api.model';
import { prepareClientSearch } from '../client-search-query.util';

/**
 * Autocomplete row: either an open {@link UiSession} from local tabs or a prototype API hit.
 */
type SearchResult =
  | { type: 'session'; value: UiSession }
  | { type: 'searchHit'; value: ClientSearchResultItem };

/**
 * PrimeNG autocomplete for finding a client by name or GUID: matches **open conversations** locally first,
 * then calls {@link ClientSearchService.searchClients} for remote hits. Remote errors surface in
 * {@link emptyMessage} (except HTTP 404); API failure analytics live on the service.
 */
@Component({
  selector: 'bn-client-search',
  imports: [
    FormsModule,
    AutoCompleteModule,
    ClientInfoComponent,
    ClientPrototypeSearchResultComponent,
    IftaLabel,
  ],
  styleUrls: ['./client-search.component.scss'],
  templateUrl: './client-search.component.html',
})
export class ClientSearchComponent {
  /** Emits when the user picks an existing local session row (not a remote-only hit). */
  @Output() clientSelected = new EventEmitter<UiSession>();

  private readonly _clientService = inject(ClientSessionService);
  private readonly _messageService = inject(MessageService);
  private readonly _clientSearchService = inject(ClientSearchService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _results = signal<SearchResult[]>([]);
  /** Rows shown in the dropdown: local sessions and/or API search hits. */
  readonly results = this._results.asReadonly();

  private readonly _loadingFromApi = signal(false);
  /** True while a remote {@link ClientSearchService.searchClients} request is in flight. */
  readonly loadingFromApi = this._loadingFromApi.asReadonly();
  /** Bound to the autocomplete model; cleared after a selection. */
  query: unknown;
  /** User-visible error text from the last failed remote search (404s are ignored). */
  emptyMessage = '';
  private readonly _remoteSearch$ = new Subject<string>();

  /**
   * Debounces remote lookups: GUID-shaped queries fire immediately; name queries wait 250ms.
   * Skipped queries ({@link prepareClientSearch} `skip`) use 0ms delay before the stream drops them.
   */
  constructor() {
    this._remoteSearch$
      .pipe(
        switchMap((raw) => {
          const prep = prepareClientSearch(raw);
          const ms =
            prep.kind === 'skip'
              ? 0
              : prep.kind === 'call' && prep.searchType === 'guid'
                ? 0
                : 250;
          return timer(ms).pipe(map(() => raw));
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((query) => this._remoteSearch(query));
  }

  /**
   * Handler for the autocomplete `completeMethod`: fills {@link results} from local sessions when
   * possible; otherwise enqueues a debounced remote search via {@link _remoteSearch$}.
   *
   * @param event - PrimeNG payload with the current input `query`.
   */
  searchClients(event: { query: string }) {
    this.emptyMessage = '';
    const raw = event.query.trim();
    const q = raw.toLowerCase();

    const localMatches = this._clientService
      .sessions()
      .filter(
        (s) =>
          s.subjectData.guid.toLowerCase().includes(q) ||
          `${s.subjectData.firstName} ${s.subjectData.lastName}`
            .toLowerCase()
            .includes(q)
      );

    if (localMatches.length > 0) {
      this._results.set(
        localMatches.map((s) => ({ type: 'session', value: s }))
      );
      this._loadingFromApi.set(false);
    } else {
      this._results.set([]);
      this._loadingFromApi.set(false);

      if (prepareClientSearch(raw).kind !== 'skip') {
        this._remoteSearch$.next(raw);
      }
    }
  }

  /**
   * Calls the prototype client search API and maps OK payloads into {@link SearchResult} `searchHit` rows.
   *
   * @param raw - Query string as entered (after any debounce), passed to {@link ClientSearchService.searchClients}.
   */
  private _remoteSearch(raw: string) {
    this._loadingFromApi.set(true);

    this._clientSearchService.searchClients(raw).subscribe({
      next: (res) => {
        if (
          res.result.code === GenericResponseCode.SERVICE_OK &&
          res.payload?.clients?.length
        ) {
          this._results.set(
            res.payload.clients.map((row) => ({
              type: 'searchHit' as const,
              value: row,
            }))
          );
        } else {
          this._results.set([]);
        }

        this._loadingFromApi.set(false);
      },
      error: (err: { message?: string; status?: number }) => {
        if (err.status !== 404) {
          this.emptyMessage = err.message ?? '';
        }
        this._results.set([]);
        this._loadingFromApi.set(false);
      },
    });
  }

  /**
   * Handles a row pick: emits {@link clientSelected} for local sessions, opens or creates a tab for
   * remote hits with a GUID, or shows a toast when the hit has no GUID.
   *
   * @param event - PrimeNG select event; `value` is a {@link SearchResult}.
   */
  onClientSelect(event: AutoCompleteSelectEvent) {
    const item = event.value as SearchResult | undefined;
    if (item?.type === 'session') {
      this.clientSelected.emit(item.value);
    } else if (item?.type === 'searchHit') {
      const row = item.value;
      if (row.guid) {
        this._clientService.createOrSelectClientConversation(row.guid);
      } else {
        this._messageService.add({
          severity: 'warn',
          summary: 'Cannot open client',
          detail: 'No person GUID is available for this result.',
          life: 5000,
        });
      }
    }
    this.query = null;
    this._results.set([]);
    this._loadingFromApi.set(false);
  }
}
